import React, { useContext } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import LoadingSpinner from "./shared/LoadingSpinner";
import Home from "../pages/Home";
import { isPrerendering } from "../utils/prerendering";
import { hasTradePlatformAccess } from "../utils/authAccess";
import { needsSubscriptionPaywall, isOnboardingIncomplete } from "../utils/onboardingStatus";
import { getAppHomeRoute } from "./DashboardSidebar/dashboardNavConfig";

/**
 * HomeRedirect component for the root "/" route
 * - Redirects authenticated users with subscription access to /trade
 * - Redirects authenticated users without subscription access to /home
 * - Redirects unauthenticated users to /home
 * - Preserves query parameters when redirecting
 */
const HomeRedirect = () => {
  const { user, isInitializing } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  // During pre-rendering, render the home page directly to avoid redirects
  // that break react-snap's static HTML generation
  if (isPrerendering()) {
    return <Home />;
  }

  // Show loading while checking auth status
  if (isInitializing) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-primary, #0f172a)',
        zIndex: 2000
      }}>
        <LoadingSpinner />
      </div>
    );
  }

  // Preserve query params
  const queryString = searchParams.toString();
  const redirectPath = queryString ? `/home?${queryString}` : '/home';

  // Logged-in subscribers: Trade workspace (Yahoo charts when the broker has no live MD)
  if (user?.token && hasTradePlatformAccess(user)) {
    if (isOnboardingIncomplete(user)) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to={getAppHomeRoute(user)} replace />;
  }

  // Logged in without paid access but not in "expired trial → paywall" state → onboarding funnel
  if (user?.token && !needsSubscriptionPaywall(user)) {
    return <Navigate to="/onboarding" replace />;
  }

  // Otherwise (not logged in, or logged in without access), go to home
  return <Navigate to={redirectPath} replace />;
};

export default HomeRedirect;

